import React from 'react';
import { Select, Button, Dropdown, Menu } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useResponsive from '../Hooks/useResponsive';
import { LanguageSwitcherProps } from '../types/types';

const { Option } = Select;

const flagStyle: React.CSSProperties = {
  marginInlineEnd: 8,
  fontSize: '16px',
};

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  type = 'dropdown',
  className = '',
  style = {},
}) => {
  const { i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const isRTL = i18n.language === 'ar' || i18n.language.startsWith('ar-');

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  ];

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem('i18nextLng', languageCode);
  };

  const renderSelect = () => (
    <Select
      value={currentLanguage.code}
      onChange={handleLanguageChange}
      style={{ width: isMobile ? 100 : 120, ...style }}
      className={className}
    >
      {languages.map((lang) => (
        <Option key={lang.code} value={lang.code}>
          <span style={flagStyle}>{lang.flag}</span>
          {lang.name}
        </Option>
      ))}
    </Select>
  );

  const renderDropdown = () => {
    const menu = (
      <Menu>
        {languages.map((lang) => (
          <Menu.Item
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 16px',
            }}
          >
            <span style={flagStyle}>{lang.flag}</span>
            <span>{lang.name}</span>
          </Menu.Item>
        ))}
      </Menu>
    );

    return (
      <Dropdown overlay={menu} trigger={['click']} placement={isRTL ? 'bottomLeft' : 'bottomRight'}>
        <Button
          type="text"
          icon={<GlobalOutlined />}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            ...style,
          }}
          className={className}
        >
          <span style={{ fontSize: '16px' }}>{currentLanguage.flag}</span>
          {!isMobile && <span>{currentLanguage.name}</span>}
        </Button>
      </Dropdown>
    );
  };

  const renderButton = () => (
    <Button
      type="text"
      icon={<GlobalOutlined />}
      onClick={() => {
        const nextLang = currentLanguage.code === 'en' ? 'ar' : 'en';
        handleLanguageChange(nextLang);
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        ...style,
      }}
      className={className}
    >
      <span style={{ fontSize: '16px' }}>{currentLanguage.flag}</span>
      {!isMobile && <span>{currentLanguage.name}</span>}
    </Button>
  );

  switch (type) {
    case 'select':
      return renderSelect();
    case 'button':
      return renderButton();
    case 'dropdown':
    default:
      return renderDropdown();
  }
};

export default LanguageSwitcher;
